import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Spin, message, Card, Space, Typography, Button, Modal } from 'antd';
import { ArrowLeftOutlined, SaveOutlined, CheckCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import GenericFormFill, { FORM_PRINT_CSS } from './GenericFormFill';
import PrintButton from '../PrintButton';
import { fetchSqlData, fetchSqlDataWithQuery, updateData } from '../../../services/api';
import { taskService } from '../TaskManagement/taskService';
import { issueTasksForForm } from '../PdfFormCreationFiles/processIssuance';
import { issueFieldTask } from '../PdfFormCreationFiles/handlersIndex';
import '../PdfFormCreationFiles/handlersIndex';
import logger from '../../../utils/logger';
import FileUploadTool from '../FileUploadTool';
import FileDownloadTool from '../FileDownloadTool'; // Import FileDownloadTool if needed
import { decodeSvgFile } from '../PdfFormCreationFiles/svgFileCodec';

interface FormField {
  name: string;
  required?: boolean;
  // Add other field properties as needed
}

// Utility: get form definition table, data table for a given route
function getFormInfoForPath(pathname: string): string[] {
  if (pathname.startsWith('/qaqc')) return ['qaqc_form_definitions', 'qaqc_form_records'];
  if (pathname.startsWith('/it')) return ['it_form_definitions', 'it_form_records'];
  if (pathname.startsWith('/to')) return ['to_form_definitions', 'to_form_records'];
  if (pathname.startsWith('/hse')) return ['hse_form_definitions', 'hse_form_records'];
  if (pathname.startsWith('/admin')) return ['adm_form_definitions', 'adm_form_records'];
  if (pathname.startsWith('/machinery')) return ['mach_form_definitions', 'mach_form_records'];
  if (pathname.startsWith('/material')) return ['mat_form_definitions', 'mat_form_records'];
  if (pathname.startsWith('/construction')) return ['const_form_definitions', 'const_form_records'];
  if (pathname.startsWith('/dcc')) return ['dcc_form_definitions', 'dcc_form_records'];
  if (pathname.startsWith('/official')) return ['off_form_definitions', 'off_form_records'];
  if (pathname.startsWith('/design')) return ['dsgn_form_definitions', 'dsgn_form_records'];
  return ['', ''];
}

/**
 * FormEdit - Shared module for editing filled forms (SVG/fields) just like FormFill
 * - Fetches the filled record from ???_form_records
 * - Fetches the form definition (SVG, fields, etc) from ???_form_definitions
 * - Renders FormFill in edit mode, passing both definition and record data
 */
const FormEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [issuing, setIssuing] = useState(false);
  const [formDef, setFormDef] = useState<any>(null);
  const [formRecord, setFormRecord] = useState<any>(null);
  const [fieldValues, setFieldValues] = useState<Record<string, any>>({});
  const [validationErrors, setValidationErrors] = useState<Record<string, boolean>>({});
  const fileUploadRef = useRef<any>(null);
  const printRootRef = useRef<HTMLDivElement>(null);

  // Do not call hooks conditionally; compute submitted status here
  const isFormSubmitted = useMemo(() => {
    const s = (formRecord?.status ?? formRecord?.doc_status ?? '').toString().toLowerCase();
    return s === 'submitted';
  }, [formRecord]);

  const isFormApproved = useMemo(() => {
    const s = (formRecord?.status ?? formRecord?.doc_status ?? '').toString().toLowerCase();
    return s === 'approved';
  }, [formRecord]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [defTable, dataTable] = getFormInfoForPath(location.pathname);
        if (!defTable || !dataTable || !id) {
          message.error('Invalid form context.');
          setLoading(false);
          return;
        }
        // Fetch the filled form record
        const recArr = await fetchSqlData(dataTable, 'id', id);
        if (!recArr || !recArr[0]) {
          message.error('Form record not found.');
          setLoading(false);
          return;
        }
        setFormRecord(recArr[0]);
        // Fetch the form definition using form_ref and form_description
        const query = `SELECT * FROM ${defTable} WHERE form_ref = '${recArr[0].form_ref}' AND form_description = '${recArr[0].form_description}'`;
        const defArr = await fetchSqlDataWithQuery(query);
        if (!defArr || !defArr[0]) {
          message.error('Form definition not found.');
          setLoading(false);
          return;
        }
        setFormDef(defArr[0]);
      } catch (err) {
        message.error('Failed to fetch form data.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [location.pathname, id]);

  useEffect(() => {
    if (formRecord) {
      logger.debug(`Upload path for form record: ${formRecord.upload_path}`);
    }
  }, [formRecord]);

  // Prepare overlay fields and values
  const overlayFields = useMemo(() => {
    return Array.isArray((formDef?.form_json as any)?.fields) ? (formDef!.form_json as any).fields : [];
  }, [formDef]);

  // Initialize field values when formRecord changes
  useEffect(() => {
    if (!formRecord) return;
    
    let parsed: any = {};
    try {
      if (typeof formRecord.fields === 'string') parsed = JSON.parse(formRecord.fields);
      else if (typeof formRecord.fields === 'object') parsed = formRecord.fields;
    } catch {
      parsed = formRecord.fields;
    }
    setFieldValues(parsed || formRecord);
  }, [formRecord]);

  if (loading) return <Spin style={{ marginTop: 40 }} />;
  if (!formDef || !formRecord) return null;

  // Validation function for required fields
  const validateFields = () => {
    const errors: Record<string, boolean> = {};
    for (const field of overlayFields) {
      if (field.required && (!fieldValues[field.name] || String(fieldValues[field.name]).trim() === '')) {
        errors[field.name] = true;
      }
    }
    setValidationErrors(errors);
    if (Object.keys(errors).length > 0) {
      message.error('Please fill all required fields before saving.');
    }
    return Object.keys(errors).length === 0;
  };

  // Issue form: update status to 'issued' and create tasks for signature signers
  const handleIssueForm = async () => {
    if (!validateFields()) return;

    Modal.confirm({
      title: 'Confirm Form Issuance',
      icon: <ExclamationCircleOutlined />,
      content: 'Issue this form and create approval tasks for signers?',
      okText: 'Issue Form',
      cancelText: 'Cancel',
      okButtonProps: { type: 'primary', danger: true },
      onOk: async () => {
        try {
          setIssuing(true);

          // Save pending edits first
          if (Object.keys(fieldValues).length > 0) {
            await handleSaveForm();
          }

          const [_, dataTable] = getFormInfoForPath(location.pathname);
          if (!dataTable) throw new Error('Invalid form context');

          // Merge latest fields
          const mergedFields: Record<string, any> = {
            ...(typeof formRecord.fields === 'object' ? formRecord.fields : {}),
            ...fieldValues,
          };

          // Update status to submitted
          await updateData(dataTable, formRecord.id, {
            ...formRecord,
            fields: JSON.stringify(mergedFields),
            status: 'submitted',
            doc_status: 'submitted',
            issued_at: new Date().toISOString(),
            issued_by: localStorage.getItem('personnel_id') || undefined,
          });

          // Create tasks via field handlers
          const formNum = formRecord?.form_num || mergedFields?.form_num || 'N/A';
          const formDesc = formRecord?.form_description || formDef?.form_description || 'Form';
          const navigateTo = `${location.pathname}`; // current edit route

          console.log('[ISSUE] Delegating task creation to handlers for fields.');
          const createdCount = await issueTasksForForm({
            overlayFields,
            mergedFields,
            formRecord,
            navigateTo,
            formNum,
            formDesc,
            strategy: 'sequential',
          });

          // Reflect status locally
          setFormRecord((prev: any) => ({
            ...prev,
            status: 'submitted',
            doc_status: 'submitted',
            fields: mergedFields,
          }));

          if (createdCount && createdCount > 0) {
            message.success('Form submitted and tasks created.');
          } else {
            message.success('Form submitted. No approval tasks were needed.');
          }
        } catch (e: any) {
          message.error(e?.message || 'Failed to issue form');
        } finally {
          setIssuing(false);
        }
      },
    });
  };

  // Handle save action for the form
  const handleSaveForm = async () => {
    if (!validateFields()) return;
    if (!formDef || !formRecord) return;
    
    setSaving(true);
    try {
      const [_, dataTable] = getFormInfoForPath(location.pathname);
      if (!dataTable) {
        throw new Error('Invalid form context');
      }

      // Capture previous merged fields to detect signature confirmations
      let prevMerged: Record<string, any> = {};
      try {
        if (typeof formRecord.fields === 'string') prevMerged = JSON.parse(formRecord.fields || '{}');
        else if (typeof formRecord.fields === 'object' && formRecord.fields) prevMerged = formRecord.fields as any;
      } catch { prevMerged = {}; }

      // Handle file uploads only if there are files queued for upload
      let uploadPath = formRecord.upload_path || null;
      if (fileUploadRef.current && fileUploadRef.current.hasFilesToUpload()) {
        const uploadResult = await fileUploadRef.current.finalizeUpload(fieldValues.form_num || formRecord.form_num);
        if (!uploadResult.success) {
          throw new Error(uploadResult.error || 'File upload failed');
        }
        uploadPath = uploadResult.folderPath || uploadPath;
      }

      // If form is already approved, lock signature fields (prevent any change/revocation)
      if (isFormApproved) {
        try {
          const signatureFields = Array.isArray(overlayFields)
            ? (overlayFields as any[]).filter((f) => (f?.type || '').toLowerCase() === 'signature')
            : [];
          const locked: Record<string, any> = { ...fieldValues };
          for (const f of signatureFields) {
            const name = String(f?.name || '');
            if (!name) continue;
            // Force current value to previous persisted value
            locked[name] = (prevMerged as any)?.[name];
          }
          // Apply lock
          if (Object.keys(locked).length) {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const _locked = locked; // no-op for types
          }
        } catch {}
      }

      // Prepare the update payload
      const payload = {
        ...formRecord,
        fields: JSON.stringify(fieldValues),
        upload_path: uploadPath,
        updated_at: new Date().toISOString()
      };

      // Update the form record
      await updateData(dataTable, formRecord.id, payload);

      // After save, detect signature field confirmations and complete tasks
      try {
        if (isFormApproved) throw new Error('skip-after-approval');
        const currentPid = localStorage.getItem('personnel_id') || '';
        const signatureFields = Array.isArray(overlayFields)
          ? (overlayFields as any[]).filter((f) => (f?.type || '').toLowerCase() === 'signature')
          : [];
        for (const f of signatureFields) {
          const name = String(f?.name || '');
          if (!name) continue;
          const beforeVal = (prevMerged as any)?.[name];
          const afterVal = (fieldValues as any)?.[name];
          const wasEmpty = !(typeof beforeVal === 'string' && beforeVal.trim().length > 0);
          const isNowFilled = typeof afterVal === 'string' && afterVal.trim().length > 0;
          if (wasEmpty && isNowFilled && currentPid) {
            await taskService.completeByFormAndField(Number(formRecord.id), name, currentPid);
          }
        }
      } catch (e: any) {
        logger.warn(`Signature task completion skipped or failed: ${e?.message || e}`);
      }

      // Detect signature revocations (previously filled -> now empty) and re-issue a task for that signature
      try {
        if (isFormApproved) throw new Error('skip-after-approval');
        const signatureFields = Array.isArray(overlayFields)
          ? (overlayFields as any[]).filter((f) => (f?.type || '').toLowerCase() === 'signature')
          : [];
        const formNum = fieldValues?.form_num || formRecord?.form_num || 'N/A';
        const formDesc = formRecord?.form_description || formDef?.form_description || 'Form';
        const navigateTo = `${location.pathname}`;
        for (const f of signatureFields) {
          const name = String(f?.name || '');
          if (!name) continue;
          const beforeVal = (prevMerged as any)?.[name];
          const afterVal = (fieldValues as any)?.[name];
          const wasFilled = typeof beforeVal === 'string' && beforeVal.trim().length > 0;
          const isNowEmpty = !(typeof afterVal === 'string' && afterVal.trim().length > 0);
          if (wasFilled && isNowEmpty) {
            await issueFieldTask({
              formRecord,
              mergedFields: fieldValues,
              fieldDef: f,
              navigateTo,
              formNum,
              formDesc,
              strategy: 'parallel',
            } as any);
          }
        }
      } catch (e: any) {
        logger.warn(`Re-issuing signature task on revoke failed: ${e?.message || e}`);
      }

      // If all signature fields are filled, auto-approve the form (status/doc_status = 'approved')
      try {
        const signatureFields = Array.isArray(overlayFields)
          ? (overlayFields as any[]).filter((f) => (f?.type || '').toLowerCase() === 'signature')
          : [];
        const allSigned = signatureFields.length === 0
          ? false
          : signatureFields.every((f: any) => {
              const name = String(f?.name || '');
              const val = (fieldValues as any)?.[name];
              return typeof val === 'string' && val.trim().length > 0;
            });
        if (allSigned && !isFormApproved) {
          await updateData(dataTable, formRecord.id, {
            ...formRecord,
            fields: JSON.stringify(fieldValues),
            status: 'approved',
            doc_status: 'approved',
          });
          // Reflect locally
          setFormRecord((prev: any) => ({
            ...prev,
            status: 'approved',
            doc_status: 'approved',
          }));
          message.success('All signatures completed. Form approved.');
        }
      } catch (e: any) {
        logger.warn(`Auto-approve check failed: ${e?.message || e}`);
      }

      // Update local state
      setFormRecord((prev: any) => ({
        ...prev,
        ...payload,
        fields: fieldValues,
      }));
      message.success('Form updated successfully!');
      
      // Navigate back to the previous page
      navigate(-1);
    } catch (err: any) {
      message.error(err?.message || 'Failed to update form');
      logger.error(`Error updating form: ${err?.message || 'Unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card style={{ margin: '0 auto', padding: 24 }}>
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <div className="no-print" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => {
                const match = location.pathname.match(/^\/(\w+)/);
                const module = match ? match[1] : '';
                if (module) {
                  navigate(`/${module}/forms/list`);
                } else {
                  navigate(-1);
                }
              }}
            >
              Back
            </Button>
            <Typography.Title level={4} style={{ margin: 0 }}>
              {formDef.form_description} — Edit
            </Typography.Title>
          </div>
          <Space>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={saving}
              onClick={handleSaveForm}
            >
              Save Changes
            </Button>
            {!isFormSubmitted && (
              <Button
                type="primary"
                danger
                icon={<CheckCircleOutlined />}
                loading={issuing}
                onClick={handleIssueForm}
              >
                Issue Form
              </Button>
            )}
            <PrintButton
              targetRef={printRootRef}
              tooltip="Print form"
              cloneRoot
              extraCss={FORM_PRINT_CSS}
            />
          </Space>
        </div>
        
        <div style={{ position: 'relative' }}>
          <GenericFormFill
            svg={formDef.svg_file ? getSvgWithSize(formDef.svg_file, (formDef?.form_json as any)?.pdfWidth, (formDef?.form_json as any)?.pdfHeight) : ''}
            fields={overlayFields}
            formSpecificFields={{}}
            formDefinition={formDef}
            fieldValues={fieldValues}
            setFieldValues={setFieldValues}
            validationErrors={validationErrors}
            loading={loading}
            colorless={false}
            hideOverlaysInView={false}
            isFieldReadOnly={(field) => {
              const t = String(field?.type || '').toLowerCase();
              if (isFormApproved) return true; // all read-only after approval
              if (isFormSubmitted) return t !== 'signature'; // only signatures remain editable when submitted
              return false; // editable before submission
            }}
            onSave={handleSaveForm}
            showPrintButton={false}
            printRootRef={printRootRef}
          />
        </div>
        
        <div className="no-print">
          <FileUploadTool
            ref={fileUploadRef}
            onSuccess={(response) => {
              if (response?.folderPath) {
                setFormRecord((prev: any) => ({
                  ...prev,
                  upload_path: response.folderPath
                }));
              }
            }}
            buttonText="Upload Files"
          />
          {formRecord?.upload_path && String(formRecord.upload_path).trim() !== '' && (
            <div style={{ marginTop: 16 }}>
              <FileDownloadTool contentPath={formRecord.upload_path} buttonText="View Attached Files" />
            </div>
          )}
        </div>
      </Space>
    </Card>
  );
};

// Helper function to process SVG similar to FormView
function getSvgWithSize(svgFile: string, pdfWidth?: number, pdfHeight?: number): string {
  try {
    const svgString = decodeSvgFile(svgFile);
    const cleanedSvgString = svgString.replace(/<svg:/g, '<').replace(/<\/svg:/g, '</');
    let vbX = 0, vbY = 0, vbW = 1000, vbH = 1400;
    const match = cleanedSvgString.match(/viewBox="([^"]+)"/);
    if (match) {
      const parts = match[1].split(' ').map(Number);
      vbX = parts[0] || 0;
      vbY = parts[1] || 0;
      vbW = parts[2];
      vbH = parts[3];
    }
    const targetW = Number(pdfWidth) || vbW;
    const targetH = Number(pdfHeight) || vbH;
    return cleanedSvgString.replace(
      /<svg([^>]*)>/,
      `<svg$1 width="${targetW}" height="${targetH}" viewBox="${vbX} ${vbY} ${vbW} ${vbH}">`
    );
  } catch (e) {
    logger.warn('Failed to decode SVG for edit');
    return '';
  }
};

export default FormEdit;
